import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import * as yup from 'yup';
import { TicketPriority, TicketStatus } from 'helpers/Enums';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import { LocalizationIds } from 'helpers/Localizations';
import { useIntl } from 'react-intl';

interface INewTicketContext {
    isLoading: boolean
    fromMessage: boolean
    selectedOrders: ITicketOrder[]
    selectedWorkOrders: ITicketWorkOrder[]
    customerId: number
    reporters: IReporterUser[]
    newTicketSchema: any

    saveNewTicket: (values: any) => void
    searchCustomers: (searchText: string, etag: number) => Promise<any>
    orderSelectionHandler: (order: ITicketOrder) => void
    workOrderSelectionHandler: (workOrder: ITicketWorkOrder) => void
    customerIdChangeHandler: (value: number) => void
}

export let newTicketContext = React.createContext({} as INewTicketContext);

interface ITicketOrder {
    orderId: number
    orderNumber: string
    orderType: string
    orderDate: moment.Moment
    orderTitle: string
}

interface ITicketWorkOrder {
    workOrderId: number
    workOrderNumber: string
    articleId: string
    articleModel: string
    articleVersion: string
    articleDescription: string
    orderId?: number
    orderNumber: string
    orderType: string
    orderDate?: moment.Moment
    orderTitle: string
}

interface IReporterUser {
    value: string
    label: string
    isExternal: boolean
    newUser: boolean
}

let { Provider } = newTicketContext;

let NewTicketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { id, customer } = useParams<{ id?: string, customer?: string }>();
    let { enqueueSnackbar } = useSnackbar();
    let history = useHistory();

    let { formatMessage } = useIntl();

    let newTicketSchema = yup.object().shape({
        title: yup.string().default('')
            .required(formatMessage(
                { id: LocalizationIds.RequiredField },
                { field: formatMessage({ id: LocalizationIds.Title }) })
            ),
        status: yup.string().default(TicketStatus.Backlog),
        customerId: yup.number().default(0).nullable(),
        customerName: yup.string().default(''),
        customerCountryCode: yup.string().default(''),
        customerCountryName: yup.string().default(''),
        ticketSubCategoryId: yup.string().default('')
            .required(formatMessage(
                { id: LocalizationIds.RequiredField },
                { field: formatMessage({ id: LocalizationIds.Category }) })
            ),
        reporterUserName: yup.string().default('')
            .required(formatMessage(
                { id: LocalizationIds.RequiredField },
                { field: formatMessage({ id: LocalizationIds.Reporter }) })
            ),
        newReporterUserName: yup.string().default('')
            .when("addNewReporter", {
                is: true,
                then: yup.string().default('')
                    .required(formatMessage(
                        { id: LocalizationIds.RequiredField },
                        { field: formatMessage({ id: LocalizationIds.Reporter }) })
                    ),
                otherwise: yup.string().default('').nullable()
            }),
        newReporterFirstName: yup.string().default('')
            .when("addNewReporter", {
                is: true,
                then: yup.string().default('')
                    .required(formatMessage(
                        { id: LocalizationIds.RequiredField },
                        { field: formatMessage({ id: LocalizationIds.FirstName }) })
                    ),
                otherwise: yup.string().default('').nullable()
            }),
        newReporterLastName: yup.string().default('')
            .when("addNewReporter", {
                is: true,
                then: yup.string().default('')
                    .required(formatMessage(
                        { id: LocalizationIds.RequiredField },
                        { field: formatMessage({ id: LocalizationIds.LastName }) })
                    ),
                otherwise: yup.string().default('').nullable()
            }),
        newReporterLanguage: yup.string().default('')
            .when("addNewReporter", {
                is: true,
                then: yup.string().default('')
                    .required(formatMessage(
                        { id: LocalizationIds.RequiredField },
                        { field: formatMessage({ id: LocalizationIds.Language }) })
                    ),
                otherwise: yup.string().default('').nullable()
            }),
        reporterIsExternal: yup.boolean().default(false),
        addNewReporter: yup.boolean().default(false),
        reporterUser: yup.string().default(''),
        customerCanView: yup.boolean().default(false),
        priority: yup.string().default(TicketPriority.Normal)
            .required(formatMessage(
                { id: LocalizationIds.RequiredField },
                { field: formatMessage({ id: LocalizationIds.Priority }) })
            ),
        workOrders: yup.array().of(yup.string()).default([]),
        orders: yup.array().default([]),
        emailId: yup.string().default(''),
        messageId: yup.string().default(id || ''),
        message: yup.string().default('')
            .when("messageId", {
                is: (messageId: string) => messageId === '',
                then: yup.string().default('').required(formatMessage({ id: LocalizationIds.PleaseSetMessage })),
                otherwise: yup.string().default('')
            })
    });

    type NewTicket = yup.InferType<typeof newTicketSchema>;

    let [isLoading, setLoading] = useState(false);

    let [selectedOrders, setSelectedOrders] = useState<ITicketOrder[]>([]);
    let [selectedWorkOrders, setSelectedWorkOrders] = useState<ITicketWorkOrder[]>([]);

    let [customerId, setCustomerId] = useState<number>(0);
    let [reporters, setReporters] = useState<IReporterUser[]>([]);

    let [fromMessage, setFromMessage] = useState<boolean>(false);

    useEffect(() => {
        if (customer != null && customer !== '')
            setCustomerId(+customer);
        if (id != null && id !== '')
            setFromMessage(true);
    }, [id, customer]);

    useEffect(() => {
        let getReporters = async () => {
            try {
                const res = await axios.get('/api/User/GetReporterUsers', { params: { customerId } });
                setReporters(res.data.items);

            } catch { }
        }

        if (customerId !== 0)
            getReporters();
    }, [customerId]);


    let saveNewTicket = async (values: NewTicket) => {
        try {
            setLoading(true);

            let request = {
                ...values,
                orders: selectedOrders,
                workOrders: selectedWorkOrders,
                customerId
            }

            let res = await axios.post('/api/Ticket/SaveNewTicket', request);

            if (res.data.affectedRecords === 1) {
                enqueueSnackbar("Ticket salvato", { variant: "success" });
                history.push(`/ticket/edit/${res.data.stringId}`);
            }

        } catch (err) { }
        finally {
            setLoading(false);
        }
    }

    let customerIdChangeHandler = (value: number) => {
        setCustomerId(value);
    }

    let searchCustomers = async (searchText: string, etag: number) => {
        if (!searchText)
            return;

        try {
            const res = await axios.post('/api/Order/CustomersQuickSearch', { searchText, etag });
            return res.data;
        } catch { }
    }

    let orderSelectionHandler = (order: ITicketOrder) => {
        let idx = selectedOrders.findIndex(f => f.orderId === order.orderId);

        if (idx !== -1)
            setSelectedOrders([...selectedOrders.filter(f => f.orderId !== order.orderId)]);
        else {
            selectedOrders.push(order);
            setSelectedOrders([...selectedOrders]);
        }

    }

    let workOrderSelectionHandler = (workOrder: ITicketWorkOrder) => {
        let idx = selectedWorkOrders.findIndex(x => x.workOrderId === workOrder.workOrderId);

        if (idx !== -1)
            setSelectedWorkOrders([...selectedWorkOrders.filter(x => x.workOrderId !== workOrder.workOrderId)]);
        else {
            selectedWorkOrders.push(workOrder);
            setSelectedWorkOrders([...selectedWorkOrders]);
        }
    }


    return (
        <Provider value={{
            isLoading,
            fromMessage,
            selectedOrders,
            selectedWorkOrders,
            customerId,
            reporters,
            newTicketSchema,

            saveNewTicket,
            searchCustomers,
            orderSelectionHandler,
            workOrderSelectionHandler,
            customerIdChangeHandler
        }}>
            {children}
        </Provider>
    );
}

export default NewTicketProvider;